import { ReactNode } from "react";
import { KeyValuePairsProps, StatusIndicator, TextContent } from "@amzn/awsui-components-console";
import SimLinkGenerator from "src/components/simLinkGenerator/SimLinkGenerator";
import { SIM_TYPES } from "src/components/simLinkGenerator/types";
import {
  HttpStatusCodes,
  ProcessedApiResponse,
  CLIENT_ERROR_RESPONSES,
  SERVER_ERROR_RESPONSES,
  SUCCESS_RESPONSES,
} from "src/api/types";
import { getWcCountryConfigByTpa, WcCountryConfig } from "src/app/wcCountryConfig";
import { Link } from "@amzn/awsui-components-console";
import { SimLinkClaimModel } from "src/app/types";

/**
 * Determines the message type from an HTTP status code.
 */
export const getMessageType = (statusCode: HttpStatusCodes): "error" | "success" | "custom" => {
  if (SERVER_ERROR_RESPONSES.includes(statusCode) || CLIENT_ERROR_RESPONSES.includes(statusCode)) {
    return "error";
  }
  if (SUCCESS_RESPONSES.includes(statusCode)) {
    return "success";
  }
  return "custom";
};

// -- Small error message components --

export const TimeoutErrorMessage = ({ claimModel }: { claimModel?: SimLinkClaimModel }) => (
  <TextContent>
    <div>
      <div>
        <b>Timeout Error</b>
        <br />
        We are seeing longer than expected delay in receiving a confirmation for your claim. Please
        note that this does not mean your claim was not successfully submitted. We kindly request
        you to keep an eye on your email inbox for a confirmation email within the next 15 minutes.
        In the event that you do not receive the email, we kindly request you to retry submitting
        your claim.
        <br />
        If you encounter any further issues, please don&apos;t hesitate to contact our{" "}
        <SimLinkGenerator target="_blank" simType={SIM_TYPES.TIMEOUT_ERROR} claimModel={claimModel}>
          Support Team
        </SimLinkGenerator>{" "}
        for assistance.
      </div>
    </div>
  </TextContent>
);

export const CargoTimeoutFailureMessage = ({ claimModel }: { claimModel?: SimLinkClaimModel }) => (
  <TextContent>
    <div>
      <div>
        <b>Timeout Error</b>
        <br />
        We are seeing longer than expected delay in receiving a confirmation for your claim.
        Unfortunately, after waiting, we were unable to confirm successful submission.
        <br />
        Please try submitting your claim again. If you encounter any further issues, please
        don&apos;t hesitate to contact our{" "}
        <SimLinkGenerator target="_blank" simType={SIM_TYPES.TIMEOUT_ERROR} claimModel={claimModel}>
          Support Team
        </SimLinkGenerator>{" "}
        for assistance.
      </div>
    </div>
  </TextContent>
);

const ServerErrorTitleText = ({ claimModel }: { claimModel?: SimLinkClaimModel }) => (
  <div>
    Oops! Something went wrong while submitting your claim. Please try again later. If the issue
    persists, please open a support{" "}
    <SimLinkGenerator target="_blank" simType={SIM_TYPES.UNKNOWN_ERROR} claimModel={claimModel}>
      ticket
    </SimLinkGenerator>
  </div>
);

/**
 * Canada-specific error content for claim submission failures.
 * Separate from CanadaErrorContent in customErrors.tsx which handles
 * the site-not-found flow with different messaging.
 */
const CanadaClaimSubmissionError = ({ config }: { config: WcCountryConfig }) => (
  <div>
    <div>
      Oops! Something went wrong while submitting your claim. Please try again later. If the issue
      persists, please perform BOTH of the following steps.
    </div>
    <ol>
      <li>
        <Link href={config.missingSiteTicketUrl} target="_blank" external>
          Click here to submit a SIM
        </Link>{" "}
        to report this issue to the Nucleus Tech team
      </li>
      <li>
        Go to{" "}
        <Link href={config.wcClaimReferralUrl} target="_blank" external>
          RIVER
        </Link>{" "}
        and submit the WC Claim referral manually to prevent a late reporting to the WC board.
      </li>
    </ol>
  </div>
);

/**
 * Result of processing the API response for display.
 */
export interface ProcessedSubmissionResult {
  data: KeyValuePairsProps.Pair[];
  titleText: ReactNode;
}

/**
 * Pure function that transforms an API response into display-ready data.
 * Handles timeout, server error (with Canada branching), and validation errors.
 */
export const buildSubmittedDataFromResponse = (
  response: ProcessedApiResponse,
  claimModel?: SimLinkClaimModel,
  workersCompensationTpa?: string
): ProcessedSubmissionResult => {
  const { statusCode } = response;
  const data: KeyValuePairsProps.Pair[] = [];
  let titleText: ReactNode = null;

  if (statusCode === HttpStatusCodes.GATEWAY_TIMEOUT) {
    titleText = (
      <div>
        Oops! Something went wrong while submitting your claim. You can find the error details below
      </div>
    );
    data.push({
      label: "",
      value: <TimeoutErrorMessage claimModel={claimModel} />,
    });
  } else if (statusCode && SERVER_ERROR_RESPONSES.includes(statusCode as HttpStatusCodes)) {
    const countryConfig = getWcCountryConfigByTpa(workersCompensationTpa);
    const isCanadaClaim = countryConfig.wcClaimReferralUrl && countryConfig.missingSiteTicketUrl;

    if (isCanadaClaim) {
      data.push({
        label: "",
        value: <CanadaClaimSubmissionError config={countryConfig} />,
      });
    } else {
      titleText = <ServerErrorTitleText claimModel={claimModel} />;
      data.push({
        label: "",
        value: (
          <TextContent>
            <div>
              {response.data?.length ? (
                <div>
                  <b>
                    Please add the following information to the ticket to better assist you with
                    this issue:
                  </b>
                </div>
              ) : null}
            </div>
          </TextContent>
        ),
      });
    }
  }

  const type = statusCode ? getMessageType(statusCode as HttpStatusCodes) : "custom";
  for (const entry of response.data) {
    data.push({
      label: entry?.fieldName,
      value:
        type === "error" && entry.errorMessage ? (
          <StatusIndicator type="error">{entry.errorMessage}</StatusIndicator>
        ) : (
          entry.fieldValue
        ),
    });
  }

  return { data, titleText };
};

/**
 * Builds the fallback error data when no response data is available.
 */
export const getUnknownErrorData = (
  claimModel?: SimLinkClaimModel,
  workersCompensationTpa?: string
): KeyValuePairsProps.Pair[] => {
  const countryConfig = getWcCountryConfigByTpa(workersCompensationTpa);
  if (countryConfig.wcClaimReferralUrl && countryConfig.missingSiteTicketUrl) {
    return [
      {
        label: "Unknown Error",
        value: <CanadaClaimSubmissionError config={countryConfig} />,
      },
    ];
  }
  return [
    {
      label: "Unknown Error",
      value: (
        <div>
          Oops! Something went wrong while submitting your claim. Please try again later. If the
          issue persists, please open a support{" "}
          <SimLinkGenerator
            target="_blank"
            simType={SIM_TYPES.UNKNOWN_ERROR}
            claimModel={claimModel}
          >
            ticket
          </SimLinkGenerator>
        </div>
      ),
    },
  ];
};
